import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserProfile } from '../hooks/useUserProfile';
import { logger } from '@/lib/logger';

export interface ProtectedRouteProps {
  children: React.ReactElement;
  requireAdmin?: boolean;
  requireCompanyOwner?: boolean;
}

interface UseUserProfileShape {
  user: unknown;
  isLoading: boolean;
  userRole?: string;
  isCompanyOwner?: boolean;
  isInitialized: boolean;
  error?: string | null;
  fetchUserProfile: () => void;
}

declare global {
  interface Window {
    __backendStatus?: { isBackendOnline?: boolean };
  }
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireCompanyOwner = false,
}) => {
  const { isAuthenticated, isLoading: auth0Loading, logout, getAccessTokenSilently } = useAuth0();
  const {
    user,
    isLoading: profileLoading,
    userRole,
    isCompanyOwner,
    isInitialized,
    error,
    fetchUserProfile,
  } = useUserProfile() as UseUserProfileShape;
  const location = useLocation();

  // Detectar errores de autenticación y cerrar sesión
  useEffect(() => {
    if (
      error &&
      (error.includes('Missing Refresh Token') ||
        error.includes('login_required') ||
        error.includes('unauthorized') ||
        error.includes('token'))
    ) {
      logger.warn(
        'Error de autenticación detectado en ProtectedRoute, pero no cerrando sesión automáticamente...'
      );
      // Comentado temporalmente para evitar cierre de sesión automático
      // logout({
      //   logoutParams: {
      //     returnTo: 'http://localhost:8080'
      //   }
      // });
    }
  }, [error, logout]);

  // Cargar perfil del usuario cuando esté autenticado
  useEffect(() => {
    if (isAuthenticated && !isInitialized && !profileLoading) {
      // Verificar si el backend está offline antes de intentar cargar el perfil
      const backendStatus = window.__backendStatus || { isBackendOnline: true };
      if (!backendStatus.isBackendOnline) {
        logger.debug('[PROTECTED_ROUTE] Backend offline, saltando carga de perfil');
        return;
      }

      logger.debug('[PROTECTED_ROUTE] Loading user profile...');
      fetchUserProfile();
    }
  }, [isAuthenticated, isInitialized, profileLoading, fetchUserProfile, getAccessTokenSilently]);

  // Mostrar loading mientras se verifica la autenticación
  if (auth0Loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Verificar autenticación
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si está autenticado pero aún cargando el perfil, mostrar loading más corto
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando perfil de usuario...</p>
        </div>
      </div>
    );
  }

  // Si está autenticado pero no tiene perfil cargado, mostrar la UI con estado offline
  if (!user && !profileLoading && isAuthenticated) {
    logger.debug('Usuario autenticado pero sin perfil, mostrando UI en modo offline...');

    // Retornar la UI completa con un estado de error en el contenido principal
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Aquí iría el layout completo con sidebar, header, etc. */}
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white shadow-lg min-h-screen">
            <div className="p-4">
              <h2 className="text-xl font-bold text-gray-800">InBest AI</h2>
            </div>
            {/* Menú de navegación */}
            <nav className="mt-8">
              <div className="px-4 py-2 text-gray-600">Dashboard</div>
              <div className="px-4 py-2 text-gray-600">Proyectos</div>
              <div className="px-4 py-2 text-gray-600">Propiedades</div>
              <div className="px-4 py-2 text-gray-600">Modelos</div>
            </nav>
          </div>

          {/* Contenido principal */}
          <div className="flex-1 p-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center">
                <div className="text-yellow-600 text-2xl mb-4">⚠️ Modo Offline</div>
                <h1 className="text-xl font-semibold text-gray-800 mb-2">
                  Servidor No Disponible
                </h1>
                <p className="text-gray-600 mb-4">
                  El backend no está respondiendo. Algunas funciones pueden no estar disponibles.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>✅ Autenticación: Funcionando</p>
                  <p>❌ Datos del usuario: No disponible</p>
                  <p>❌ Proyectos: No disponible</p>
                  <p>❌ Propiedades: No disponible</p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Reintentar Conexión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si requiere admin, verificar que el usuario sea administrador
  if (requireAdmin && userRole !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Si requiere propietario de empresa, verificar que el usuario sea propietario
  if (requireCompanyOwner && !isCompanyOwner) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Si está autenticado y tiene perfil, mostrar el contenido protegido
  return children;
};

export default ProtectedRoute;
